import { SourceLinks } from '@/components/ui/source-links';
import { Badge } from '@/components/ui/badge';
import { Scroll } from 'lucide-react';
import { ImportanceBadge } from '../ui/importance-badge';
import { DatePopover } from '../ui/date-popover';
import { DiscoveryCard } from './discovery-card';
import { cn } from '@/lib/utils';

interface AuthorSourceCardProps {
    source: any; // Using any for brevity, should match AuthorSource type
    isExpanded: boolean;
    onToggleExpansion: () => void;
    title?: string;
}

export function AuthorSourceCard({
    source,
    isExpanded,
    onToggleExpansion,
    title
}: AuthorSourceCardProps) {
    return (
        <DiscoveryCard
            isExpanded={isExpanded}
            onToggle={onToggleExpansion}
            header={
                <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                        <Scroll className="h-5 w-5 text-primary/70" />
                        <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="secondary" className="text-[10px] uppercase tracking-widest bg-muted/20">
                                {source.sourceType.replace(/-/g, ' ')}
                            </Badge>
                            {source.personName && (
                                <span className="font-serif font-medium text-lg text-foreground/90">{source.personName}</span>
                            )}
                            {source.sourceName && (
                                <span className="font-serif font-medium text-lg text-foreground/90">{source.sourceName}</span>
                            )}
                            <ImportanceBadge importance={source.importance} />
                            <Badge
                                variant="outline"
                                className={`text-[9px] uppercase tracking-tighter ${source.confidence === 'high' ? 'border-green-500/50 text-green-600' :
                                    source.confidence === 'medium' ? 'border-blue-500/50 text-blue-600' :
                                        'border-yellow-500/50 text-yellow-600'
                                    }`}
                            >
                                {source.confidence} confidence
                            </Badge>
                        </div>
                    </div>
                    <p className={cn(
                        "text-sm text-foreground/70 italic font-serif transition-all duration-300",
                        !isExpanded && "line-clamp-2"
                    )}>
                        {source.description}
                    </p>
                </div>
            }
        >
            <div className="space-y-4 mt-6">
                {source.evidence && (
                    <div className="space-y-1.5 border-l border-primary/10 pl-6">
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/60">Evidence</div>
                        <p className="text-sm text-foreground/70 font-light leading-relaxed">{source.evidence}</p>
                    </div>
                )}

                {source.informedSections?.length > 0 && (
                    <div className="space-y-3">
                        <div className="text-[10px] font-bold text-foreground/50 uppercase tracking-[0.2em] border-b border-border/10 pb-2">Informed Sections</div>
                        <div className="space-y-3">
                            {source.informedSections.map((section: any, idx: number) => (
                                <div key={idx} className="space-y-2 border-l border-primary/10 pl-4">
                                    <div className="flex gap-2 flex-wrap mb-1">
                                        {section.passages.map((passage: string, pIdx: number) => (
                                            <Badge key={pIdx} variant="outline" className="text-[10px] bg-background/50 border-primary/10">
                                                {passage}
                                            </Badge>
                                        ))}
                                    </div>
                                    <p className="text-xs text-foreground/60 leading-relaxed italic">{section.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {source.acquisition && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2 border-l border-primary/10 pl-6">
                            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/60">Acquisition Context</div>
                            <div className="text-sm space-y-2">
                                {source.acquisition.when && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] uppercase text-muted-foreground/60 tracking-wider">When:</span>
                                        <DatePopover date={source.acquisition.when} title="Acquisition Date" />
                                    </div>
                                )}
                                {source.acquisition.where && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] uppercase text-muted-foreground/60 tracking-wider">Where:</span>
                                        <span className="text-foreground/70">{source.acquisition.where}</span>
                                    </div>
                                )}
                                {source.acquisition.context && (
                                    <p className="text-[13px] text-foreground/60 italic font-light">
                                        {source.acquisition.context}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {source.sources?.length > 0 && (
                    <div className="pt-4 border-t border-border/10">
                        <SourceLinks sources={source.sources} />
                    </div>
                )}
            </div>
        </DiscoveryCard>
    );
}
